import { useEffect, useState } from "react";
import { usePatientsStore } from "@/lib/stores/patients";
import { useDoctorsStore } from "@/lib/stores/doctors";
import { usePrescriptionsStore } from "@/lib/stores/prescriptions";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle2, Stethoscope, Printer } from "lucide-react";
import { printPatientOPSheet } from "@/lib/utils/printUtils";
import { printPrescription } from "@/lib/utils/printPrescription";
import { PatientStatus, Patient } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { safeFormatTime } from '@/lib/utils/dateUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface PatientQueueProps {
  status: PatientStatus | "all";
  filteredPatients?: Patient[];
}

export function PatientQueue({ status, filteredPatients }: PatientQueueProps) {
  const allPatients = usePatientsStore((state) => state.patients);
  const updatePatientStatus = usePatientsStore((state) => state.updatePatientStatus);
  const updatePatientDoctor = usePatientsStore((state) => state.updatePatientDoctor);
  const { doctors } = useDoctorsStore();
  const { getPrescriptionsByPatient, fetchPrescriptions } = usePrescriptionsStore();
  const { toast } = useToast();
  const [selectedDoctorForPatient, setSelectedDoctorForPatient] = useState<Record<string, string>>({});
  const [assignDialogOpen, setAssignDialogOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    void fetchPrescriptions();
  }, [fetchPrescriptions]);

  const patientHasIssuedPrescription = (patientId: string) =>
    getPrescriptionsByPatient(patientId).some((p) => p.status === "issued");
  
  // Use filtered patients if provided, otherwise use all patients
  const patients = filteredPatients || allPatients;
  // For staff dashboard, show ALL statuses in one table when status is "all"
  const displayPatients = status === "all" ? patients : patients.filter((patient) => patient.status === status);

  const getEmptyMessage = () => {
    switch (status) {
      case "waiting":
        return "No patients waiting";
      case "in-consultation":
        return "No patients in consultation";
      case "completed":
        return "No completed consultations";
      default:
        return "No patients";
    }
  };

  const getPaymentStatus = (patient: Patient) => {
    // Check if services exist and calculate total
    const hasServices = patient.services && Array.isArray(patient.services) && patient.services.length > 0;
    const servicesCost = hasServices 
      ? patient.services.reduce((total: number, service: any) => {
          const price = typeof service.price === "number" ? service.price : parseFloat(service.price) || 0;
          return total + price;
        }, 0)
      : 0;
    const totalAmount = servicesCost;
    const amountPaid = patient.amountPaid || 0;
    
    // For completed patients with no services (bill generated), show "Bills Cleared"
    if (patient.status === "completed" && totalAmount === 0 && !hasServices) {
      return (
        <span className="text-[10px] sm:text-xs text-blue-600 font-medium whitespace-nowrap">Cleared</span>
      );
    }
    
    // If there are services, always show payment status
    if (hasServices && totalAmount > 0) {
      if (amountPaid >= totalAmount) {
        return (
          <span className="text-[10px] sm:text-xs text-green-600 font-medium whitespace-nowrap">₹{amountPaid}</span>
        );
      }
      const due = totalAmount - amountPaid;
      return (
        <span className="text-[10px] sm:text-xs text-red-600 font-medium whitespace-nowrap">₹{due}</span>
      );
    }
    
    // If services exist but total is 0 (free services)
    if (hasServices && totalAmount === 0) {
      return (
        <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Free</span>
      );
    }
    
    // If no services and no payment
    return <span className="text-[10px] sm:text-xs text-gray-400">-</span>;
  };

  const formatRegisteredDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = date.getDate();
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return "-";
    }
  };

  const formatRegisteredTime = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      return safeFormatTime(dateString);
    } catch {
      return "-";
    }
  };

  const formatMobileNumber = (contactNumber: string | null | undefined): string => {
    if (!contactNumber) return "-";
    // Remove all non-digit characters and common country prefixes.
    let cleaned = contactNumber.replace(/[^\d]/g, "");
    if (cleaned.startsWith("91") && cleaned.length >= 12) {
      cleaned = cleaned.substring(2);
    }
    if (cleaned.startsWith("91") && cleaned.length >= 12) {
      cleaned = cleaned.substring(2);
    }
    return cleaned.trim() || "-";
  };

  const getStatusBadge = (patientStatus: PatientStatus) => {
    switch (patientStatus) {
      case "waiting":
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs whitespace-nowrap">
            Waiting
          </Badge>
        );
      case "in-consultation":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] sm:text-xs whitespace-nowrap px-1.5">
            <span className="hidden xl:inline">In Consultation</span>
            <span className="xl:hidden">Consult</span>
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs whitespace-nowrap">
            Completed
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleAssignDoctor = (patientId: string) => {
    const doctorId = selectedDoctorForPatient[patientId];
    if (!doctorId || doctorId === "no_doctors_available") {
      toast({
        title: "Error",
        description: "Please select a doctor",
        variant: "destructive"
      });
      return;
    }

    try {
      updatePatientDoctor(patientId, doctorId);
      updatePatientStatus(patientId, "in-consultation", doctorId);
      setAssignDialogOpen(prev => ({ ...prev, [patientId]: false }));
      setSelectedDoctorForPatient(prev => ({ ...prev, [patientId]: "" }));
      toast({
        title: "Success",
        description: "Doctor assigned successfully"
      });
    } catch (error) {
      console.error("Error assigning doctor:", error);
      toast({
        title: "Error",
        description: "Failed to assign doctor",
        variant: "destructive"
      });
    }
  };

  if (displayPatients.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        {getEmptyMessage()}
      </div>
    );
  }

  const thClass =
    "h-auto px-1.5 py-2 text-left font-semibold text-[11px] leading-tight sm:text-xs whitespace-normal";
  const tdClass = "px-1.5 py-2 text-left align-top text-[11px] sm:text-xs";

  return (
    <div className="border rounded-lg overflow-hidden w-full max-w-full">
      <div className="w-full max-w-full [&>div]:overflow-x-hidden">
          <Table className="table-fixed w-full max-w-full text-xs">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className={`${thClass} w-[17%]`}>Patient</TableHead>
                <TableHead className={`${thClass} w-[11%]`}>
                  <span className="block">Registered</span>
                </TableHead>
                <TableHead className={`${thClass} w-[11%] hidden md:table-cell`}>Mobile</TableHead>
                <TableHead className={`${thClass} w-[12%] hidden md:table-cell`}>Location</TableHead>
                <TableHead className={`${thClass} w-[14%] hidden md:table-cell`}>Reason</TableHead>
                <TableHead className={`${thClass} w-[11%]`}>Status</TableHead>
                <TableHead className={`${thClass} w-[10%] hidden sm:table-cell`}>Payment</TableHead>
                <TableHead className={`${thClass} w-[14%] text-center`}>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayPatients.map((patient) => (
                <TableRow key={patient.id} className="hover:bg-gray-50">
                  <TableCell className={tdClass}>
                    <div className="min-w-0">
                      <Link 
                        to={`/patient/${patient.id}`} 
                        className="hover:text-hospital-primary transition-colors font-semibold text-gray-900 block truncate"
                        title={patient.name}
                      >
                        {patient.name}
                      </Link>
                      <span className="text-[10px] sm:text-xs text-gray-600 whitespace-nowrap">
                        {patient.age} / {patient.gender}
                      </span>
                      {/* Mobile: Show mobile number and location below name */}
                      <div className="md:hidden mt-1 space-y-0.5">
                        <span className="text-xs text-gray-500">
                          📱 {formatMobileNumber(patient.contactNumber)}
                        </span>
                        {patient.location && (
                          <span className="text-xs text-gray-500 block">
                            📍 {patient.location}
                          </span>
                        )}
                        {patient.reason && (
                          <span className="text-xs text-gray-500 block">
                            💬 {patient.reason}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className={tdClass}>
                    <div className="flex flex-col min-w-0 leading-tight">
                      <span className="text-gray-700 font-medium whitespace-nowrap">
                        {formatRegisteredTime(patient.registeredAt)}
                      </span>
                      <span className="text-[10px] text-gray-600 whitespace-nowrap">
                        {formatRegisteredDate(patient.registeredAt)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={`${tdClass} hidden md:table-cell`}>
                    <span className="block truncate text-gray-700" title={formatMobileNumber(patient.contactNumber)}>
                      {formatMobileNumber(patient.contactNumber)}
                    </span>
                  </TableCell>
                  <TableCell className={`${tdClass} hidden md:table-cell`}>
                    <span className="block truncate text-gray-700" title={patient.location || "-"}>
                      {patient.location || "-"}
                    </span>
                  </TableCell>
                  <TableCell className={`${tdClass} hidden md:table-cell`}>
                    <span className="block truncate text-gray-700" title={patient.reason || "-"}>
                      {patient.reason || "-"}
                    </span>
                  </TableCell>
                  <TableCell className={tdClass}>
                    {getStatusBadge(patient.status)}
                  </TableCell>
                  <TableCell className={`${tdClass} hidden sm:table-cell`}>
                    {getPaymentStatus(patient)}
                  </TableCell>
                  <TableCell className={`${tdClass} text-center`}>
                    {patient.status === "waiting" ? (
                      <Dialog open={assignDialogOpen[patient.id] || false} onOpenChange={(open) => setAssignDialogOpen(prev => ({ ...prev, [patient.id]: open }))}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="h-7 border-hospital-primary text-hospital-primary hover:bg-hospital-primary/10 text-[10px] px-2" title="Assign doctor">
                            Assign
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Assign Doctor</DialogTitle>
                            <DialogDescription>
                              Select a doctor to assign to {patient.name}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="py-4">
                            <Select 
                              value={selectedDoctorForPatient[patient.id] || ""} 
                              onValueChange={(value) => setSelectedDoctorForPatient(prev => ({ ...prev, [patient.id]: value }))}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a doctor" />
                              </SelectTrigger>
                              <SelectContent>
                                {doctors.length > 0 ? (
                                  doctors.map((doctor) => (
                                    <SelectItem key={doctor.id} value={doctor.id}>
                                      {doctor.name} - {doctor.specialization}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="no_doctors_available" disabled>
                                    No doctors available
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            <Button 
                              className="mt-4 w-full"
                              onClick={() => handleAssignDoctor(patient.id)}
                            >
                              Assign Doctor
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    ) : patient.status === "in-consultation" ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-7 border-hospital-success text-hospital-success hover:bg-hospital-success/10 text-[10px] px-2"
                        title="Mark completed"
                        onClick={() => updatePatientStatus(patient.id, "completed")}
                      >
                        Done
                      </Button>
                    ) : patient.status === "completed" ? (
                      patientHasIssuedPrescription(patient.id) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0 border-gray-300 text-gray-600 hover:bg-gray-50"
                          title="Print Prescription"
                          onClick={() => {
                            const patientPrescriptions = getPrescriptionsByPatient(patient.id);
                            printPrescription(patient, patientPrescriptions, doctors);
                          }}
                        >
                          <Printer className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0 border-gray-300 text-gray-600 hover:bg-gray-50"
                          title="Print OP Sheet"
                          onClick={() => printPatientOPSheet(patient, doctors)}
                        >
                          <Printer className="h-3.5 w-3.5" />
                        </Button>
                      )
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
      </div>
    </div>
  );
}